import { DEFAULT_STUDIO_CHUNK_SIZE_METERS } from "./coordinates.js";
import { validateAssetDefinitionId } from "./asset-authoring.js";
import { FOOTPRINT_EDGE_EPSILON_METERS, footprintWorldBounds, footprintsOverlap } from "./footprints.js";
import { validateWorldStudioScatterRules } from "./scatter-rules.js";
import { validateWorldStudioScatterExclusionLayer, WORLD_STUDIO_SCATTER_EXCLUSION_LAYER_TYPE } from "./scatter-exclusion-schema.js";
import { validateWorldStudioGeneratedObjectSuppressions } from "./generated-object-suppression-schema.js";
import { validateWorldStudioSplines } from "./spline-schema.js";
import { validateWorldStudioSplineProfiles } from "./spline-profile-schema.js";
import { validateWorldStudioRoads } from "./road-source.js";
export const WORLD_STUDIO_PROJECT_SCHEMA = "endclock.world-studio/1";
export const WORLD_STUDIO_GENERATOR_ID = "studio-heightfield";
export const WORLD_STUDIO_GENERATOR_VERSION = 1;
export const WORLD_STUDIO_BASE_MODE = "flat";
export const WORLD_STUDIO_GENERATED_BASE_MODE = "generated";
export const WORLD_STUDIO_BIOME_VERSION = 1;
export const WORLD_STUDIO_STARTER_BIOMES = Object.freeze([
    Object.freeze({
        id: "ashland",
        version: WORLD_STUDIO_BIOME_VERSION,
        name: "Ashland",
        description: "Wind-scoured ash fields with exposed, abrasive ground.",
        terrain: Object.freeze({
            mode: "modifier",
            reliefMultiplier: 0.85,
            roughnessStrength: 0.72,
            localDetailStrength: 0.62,
        }),
        material: Object.freeze({ surfaceId: "ash-dust" }),
        scatterRuleIds: Object.freeze(["charred-scrub"]),
        tags: Object.freeze(["arid", "ash-covered"]),
    }),
    Object.freeze({
        id: "crater-wastes",
        version: WORLD_STUDIO_BIOME_VERSION,
        name: "Crater Wastes",
        description: "Broken impact regolith shaped into severe exposed relief.",
        terrain: Object.freeze({
            mode: "modifier",
            reliefMultiplier: 1.4,
            roughnessStrength: 1.05,
            localDetailStrength: 0.42,
        }),
        material: Object.freeze({ surfaceId: "impact-regolith" }),
        scatterRuleIds: Object.freeze(["crater-debris"]),
        tags: Object.freeze(["barren", "impact-zone", "radiation"]),
    }),
    Object.freeze({
        id: "fertile-lowlands",
        version: WORLD_STUDIO_BIOME_VERSION,
        name: "Fertile Lowlands",
        description: "Sheltered low country with subdued terrain and living soil.",
        terrain: Object.freeze({
            mode: "modifier",
            reliefMultiplier: 0.55,
            roughnessStrength: 0.18,
            localDetailStrength: 0.12,
        }),
        material: Object.freeze({ surfaceId: "fertile-soil" }),
        scatterRuleIds: Object.freeze(["lowland-flora"]),
        tags: Object.freeze(["fertile", "lowland"]),
    }),
]);
export const DEFAULT_ROLLING_HILLS_RECIPE = Object.freeze({
    id: "rolling-hills",
    reliefMeters: 78,
    macroWavelengthMeters: 520,
    regionalWavelengthMeters: 190,
    localWavelengthMeters: 68,
    regionalStrength: 0.36,
    localStrength: 0.14,
    warpMeters: 72,
});
export const DEFAULT_MOUNTAIN_RIDGE_RECIPE = Object.freeze({
    id: "mountain-ridge",
    reliefMeters: 300,
    macroWavelengthMeters: 900,
    ridgeWavelengthMeters: 420,
    regionalWavelengthMeters: 180,
    ridgeSharpness: 1.7,
    valleyStrength: 0.35,
    localStrength: 0.06,
    warpMeters: 120,
});
export const DEFAULT_CRATER_BASIN_RECIPE = Object.freeze({
    id: "crater-basin",
    centerX: 0,
    centerZ: 0,
    radiusMeters: 150,
    depthMeters: 105,
    rimHeightMeters: 42,
    rimWidthMeters: 45,
    floorRadiusMeters: 40,
});
const PROJECT_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const WORLD_STUDIO_PROJECT_ID_MAX_LENGTH = 64;
const MAX_CATALOG_ID_LENGTH = 96;
export const WORLD_STUDIO_MAX_AUTHORED_INSTANCES = 10_000;
export const WORLD_STUDIO_MAX_INSTANCE_HEIGHT_OFFSET_METERS = 10_000;
const AUTHORED_INSTANCE_ID_PATTERN = /^[a-z0-9]+(?:[-_][a-z0-9]+)*$/;
const MAX_BIOME_NAME_LENGTH = 96;
const MAX_BIOME_DESCRIPTION_LENGTH = 512;
const MAX_BIOME_DEFINITIONS = 1_024;
const MAX_BIOME_LIST_ENTRIES = 64;
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001f\u007f]/;
const MAX_CHUNK_SIZE_METERS = 4_096;
const MIN_SAMPLE_SPACING_METERS = 0.01;
export const WORLD_STUDIO_TERRAIN_HEIGHT_DELTA_LAYER_TYPE = "terrain-height-delta";
export const WORLD_STUDIO_TERRAIN_HEIGHT_DELTA_LAYER_VERSION = 1;
export const MAX_TERRAIN_DELTA_METERS = 10_000;
export const WORLD_STUDIO_BIOME_MASK_LAYER_TYPE = "terrain-biome-mask";
export const WORLD_STUDIO_BIOME_MASK_LAYER_VERSION = 1;
export const WORLD_STUDIO_MASK_WEIGHT_EPSILON = 1e-6;
export const WORLD_STUDIO_GROUND_MATERIAL_LAYER_TYPE = "terrain-ground-material";
export const WORLD_STUDIO_GROUND_MATERIAL_LAYER_VERSION = 1;
export const WORLD_STUDIO_FOUNDATION_GRADE_LAYER_TYPE = "terrain-foundation-grade";
export const WORLD_STUDIO_FOUNDATION_GRADE_LAYER_VERSION = 1;
/** Bounds transient indexing work even for hostile/future very large operators. */
export const WORLD_STUDIO_MAX_FOUNDATION_CHUNKS = 4_096;
export const WORLD_STUDIO_GROUND_MATERIAL_IDS = Object.freeze(["default", "ash", "soil", "rock", "grass"]);
export class WorldStudioProjectValidationError extends TypeError {
    path;
    constructor(path, reason) {
        super(`Invalid World Studio project at ${path}: ${reason}`);
        this.path = path;
        this.name = "WorldStudioProjectValidationError";
    }
}
export function createDefaultWorldStudioProject() {
    return validateWorldStudioProject({
        schema: WORLD_STUDIO_PROJECT_SCHEMA,
        projectId: "untitled",
        revision: 0,
        generator: {
            id: WORLD_STUDIO_GENERATOR_ID,
            version: WORLD_STUDIO_GENERATOR_VERSION,
            seed: 734_021,
        },
        grid: {
            chunkSizeMeters: DEFAULT_STUDIO_CHUNK_SIZE_METERS,
            sampleSpacingMeters: 1,
        },
        bounds: {
            minX: -4_096,
            minZ: -4_096,
            maxX: 4_096,
            maxZ: 4_096,
        },
        base: {
            mode: WORLD_STUDIO_BASE_MODE,
            heightMeters: 0,
        },
        assetCatalog: {
            id: "default",
            version: 1,
        },
        biomes: WORLD_STUDIO_STARTER_BIOMES,
        layers: [],
        objects: [],
    });
}
/** Validates an unknown value and returns an owned, canonical, deeply frozen document. */
export function validateWorldStudioProject(value) {
    const project = requireRecord(value, "$", [
        "schema",
        "projectId",
        "revision",
        "generator",
        "grid",
        "bounds",
        "base",
        "assetCatalog",
        "biomes",
        "layers",
        "objects",
    ], ["authoredInstances", "scatterRules", "generatedObjectSuppressions", "splines", "splineProfiles", "roads"]);
    if (project.schema !== WORLD_STUDIO_PROJECT_SCHEMA) {
        invalid("$.schema", `unsupported schema; expected ${WORLD_STUDIO_PROJECT_SCHEMA}`);
    }
    const projectId = validateWorldStudioProjectId(project.projectId);
    const revision = requireNonNegativeSafeInteger(project.revision, "$.revision");
    const generator = requireRecord(project.generator, "$.generator", ["id", "version", "seed"]);
    if (generator.id !== WORLD_STUDIO_GENERATOR_ID) {
        invalid("$.generator.id", `unsupported generator; expected ${WORLD_STUDIO_GENERATOR_ID}`);
    }
    if (generator.version !== WORLD_STUDIO_GENERATOR_VERSION) {
        invalid("$.generator.version", `unsupported generator version; expected ${WORLD_STUDIO_GENERATOR_VERSION}`);
    }
    const seed = requireSafeInteger(generator.seed, "$.generator.seed");
    const grid = requireRecord(project.grid, "$.grid", ["chunkSizeMeters", "sampleSpacingMeters"]);
    const chunkSizeMeters = requireSafeInteger(grid.chunkSizeMeters, "$.grid.chunkSizeMeters");
    if (chunkSizeMeters < 1 || chunkSizeMeters > MAX_CHUNK_SIZE_METERS) {
        invalid("$.grid.chunkSizeMeters", `must be between 1 and ${MAX_CHUNK_SIZE_METERS}`);
    }
    const sampleSpacingMeters = requireFiniteNumber(grid.sampleSpacingMeters, "$.grid.sampleSpacingMeters");
    if (sampleSpacingMeters < MIN_SAMPLE_SPACING_METERS || sampleSpacingMeters > chunkSizeMeters) {
        invalid("$.grid.sampleSpacingMeters", `must be between ${MIN_SAMPLE_SPACING_METERS} and chunkSizeMeters`);
    }
    const bounds = requireRecord(project.bounds, "$.bounds", ["minX", "minZ", "maxX", "maxZ"]);
    const minX = requireFiniteNumber(bounds.minX, "$.bounds.minX");
    const minZ = requireFiniteNumber(bounds.minZ, "$.bounds.minZ");
    const maxX = requireFiniteNumber(bounds.maxX, "$.bounds.maxX");
    const maxZ = requireFiniteNumber(bounds.maxZ, "$.bounds.maxZ");
    if (minX >= maxX)
        invalid("$.bounds", "minX must be less than maxX");
    if (minZ >= maxZ)
        invalid("$.bounds", "minZ must be less than maxZ");
    const base = validateBase(project.base);
    const assetCatalog = requireRecord(project.assetCatalog, "$.assetCatalog", ["id", "version"]);
    const catalogId = requireIdentifier(assetCatalog.id, "$.assetCatalog.id", MAX_CATALOG_ID_LENGTH);
    const catalogVersion = requirePositiveSafeInteger(assetCatalog.version, "$.assetCatalog.version");
    const biomes = validateWorldStudioBiomes(project.biomes);
    const scatterRules = validateWorldStudioScatterRules(Object.hasOwn(project, "scatterRules") ? project.scatterRules : []);
    const layers = validateWorldStudioLayers(project.layers, { chunkSizeMeters, sampleSpacingMeters }, { minX, minZ, maxX, maxZ }, biomes);
    requireEmptyArray(project.objects, "$.objects");
    const authoredInstances = validateWorldStudioAuthoredInstances(Object.hasOwn(project, "authoredInstances") ? project.authoredInstances : [], { minX, minZ, maxX, maxZ });
    validateFoundationPairing(layers, authoredInstances, chunkSizeMeters);
    const generatedObjectSuppressions = validateWorldStudioGeneratedObjectSuppressions(Object.hasOwn(project, "generatedObjectSuppressions") ? project.generatedObjectSuppressions : []);
    const splines = validateWorldStudioSplines(Object.hasOwn(project, "splines") ? project.splines : [], { minX, minZ, maxX, maxZ });
    const splineProfiles = Object.hasOwn(project, "splineProfiles") ? validateWorldStudioSplineProfiles(project.splineProfiles, splines) : [];
    const roads = Object.hasOwn(project, "roads") ? validateWorldStudioRoads(project.roads, splines) : [];
    return Object.freeze({
        schema: WORLD_STUDIO_PROJECT_SCHEMA,
        projectId,
        revision,
        generator: Object.freeze({
            id: WORLD_STUDIO_GENERATOR_ID,
            version: WORLD_STUDIO_GENERATOR_VERSION,
            seed,
        }),
        grid: Object.freeze({ chunkSizeMeters, sampleSpacingMeters }),
        bounds: Object.freeze({ minX, minZ, maxX, maxZ }),
        base,
        assetCatalog: Object.freeze({ id: catalogId, version: catalogVersion }),
        biomes,
        scatterRules,
        layers,
        objects: Object.freeze([]),
        authoredInstances,
        generatedObjectSuppressions,
        splines,
        ...(splineProfiles.length ? { splineProfiles } : {}),
        ...(roads.length ? { roads } : {}),
    });
}
/** Structural validation deliberately preserves well-formed unresolved definition references. */
export function validateWorldStudioAuthoredInstance(value, bounds, path = "$.authoredInstance") {
    const entry = requireRecord(value, path, ["id", "assetId", "position", "yawDeg", "heightOffsetMeters"], ["scale"]);
    if (typeof entry.id !== "string" || entry.id.length > 128 || !AUTHORED_INSTANCE_ID_PATTERN.test(entry.id)) {
        invalid(`${path}.id`, "must be a lowercase authored instance ID of at most 128 characters, without a definition namespace");
    }
    let assetId;
    try {
        assetId = validateAssetDefinitionId(entry.assetId);
    }
    catch {
        invalid(`${path}.assetId`, "must be a lowercase namespaced asset definition ID of at most 120 characters");
    }
    const position = requireRecord(entry.position, `${path}.position`, ["x", "z"]);
    const x = requireFiniteNumber(position.x, `${path}.position.x`);
    const z = requireFiniteNumber(position.z, `${path}.position.z`);
    if (!isWithinWorldStudioBounds(bounds, x, z))
        invalid(`${path}.position`, "must lie within project bounds (minimum inclusive, maximum exclusive)");
    const yawDeg = normalizeWorldStudioYaw(requireFiniteNumber(entry.yawDeg, `${path}.yawDeg`));
    const heightOffsetMeters = requireFiniteNumber(entry.heightOffsetMeters, `${path}.heightOffsetMeters`);
    if (Math.abs(heightOffsetMeters) > WORLD_STUDIO_MAX_INSTANCE_HEIGHT_OFFSET_METERS) {
        invalid(`${path}.heightOffsetMeters`, `absolute offset must be at most ${WORLD_STUDIO_MAX_INSTANCE_HEIGHT_OFFSET_METERS} meters`);
    }
    const scale = Object.hasOwn(entry, "scale") ? requireFiniteNumber(entry.scale, `${path}.scale`) : undefined;
    if (scale !== undefined && (scale <= 0 || scale > 8))
        invalid(`${path}.scale`, "must be greater than zero and at most 8");
    return Object.freeze({ id: entry.id, assetId, position: Object.freeze({ x, z }), yawDeg, heightOffsetMeters,
        ...(scale === undefined ? {} : { scale }) });
}
export function validateWorldStudioAuthoredInstances(value, bounds) {
    if (!Array.isArray(value))
        invalid("$.authoredInstances", "must be an array");
    if (value.length > WORLD_STUDIO_MAX_AUTHORED_INSTANCES)
        invalid("$.authoredInstances", `must contain at most ${WORLD_STUDIO_MAX_AUTHORED_INSTANCES} instances`);
    const seen = new Set();
    const instances = Array.from(value, (entry, index) => {
        const instance = validateWorldStudioAuthoredInstance(entry, bounds, `$.authoredInstances[${index}]`);
        if (seen.has(instance.id))
            invalid(`$.authoredInstances[${index}].id`, "duplicate authored instance ID");
        seen.add(instance.id);
        return instance;
    });
    return Object.freeze(instances.sort((a, b) => compareCanonicalStrings(a.id, b.id)));
}
export function normalizeWorldStudioYaw(yawDeg) {
    if (typeof yawDeg !== "number" || !Number.isFinite(yawDeg))
        throw new RangeError("Yaw must be a finite number of degrees");
    const remainder = yawDeg % 360;
    const normalized = remainder < 0 ? remainder + 360 : remainder;
    return normalized === 0 || normalized === 360 ? 0 : normalized;
}
/** Builds an ordinary editable v1 biome with conservative real defaults. */
export function createWorldStudioBiomeDefinition(input) {
    const candidate = {
        id: input.id,
        version: WORLD_STUDIO_BIOME_VERSION,
        name: input.name,
    };
    if (input.description !== undefined)
        candidate.description = input.description;
    candidate.terrain = input.terrain ?? { mode: "inherit" };
    candidate.material = input.material ?? { surfaceId: "studio-neutral" };
    candidate.scatterRuleIds = input.scatterRuleIds ?? [];
    candidate.tags = input.tags ?? [];
    return validateWorldStudioBiomeDefinition(candidate);
}
export function validateWorldStudioBiomeId(value, path = "$.biomeId") {
    return requireIdentifier(value, path, WORLD_STUDIO_PROJECT_ID_MAX_LENGTH);
}
export function isWorldStudioProjectId(value) {
    return typeof value === "string"
        && value.length >= 1
        && value.length <= WORLD_STUDIO_PROJECT_ID_MAX_LENGTH
        && PROJECT_ID_PATTERN.test(value);
}
export function validateWorldStudioProjectId(value, path = "$.projectId") {
    if (!isWorldStudioProjectId(value)) {
        invalid(path, `must be a lowercase slug of at most ${WORLD_STUDIO_PROJECT_ID_MAX_LENGTH} characters`);
    }
    return value;
}
/** Validates, owns and deeply freezes one canonical custom biome definition. */
export function validateWorldStudioBiomeDefinition(value, path = "$.biome") {
    const biome = requireRecord(value, path, ["id", "version", "name", "terrain", "material", "scatterRuleIds", "tags"], ["description"]);
    const id = requireIdentifier(biome.id, `${path}.id`, WORLD_STUDIO_PROJECT_ID_MAX_LENGTH);
    if (biome.version !== WORLD_STUDIO_BIOME_VERSION) {
        invalid(`${path}.version`, `unsupported biome version; expected ${WORLD_STUDIO_BIOME_VERSION}`);
    }
    const name = requireDisplayText(biome.name, `${path}.name`, MAX_BIOME_NAME_LENGTH);
    const description = Object.hasOwn(biome, "description")
        ? requireDisplayText(biome.description, `${path}.description`, MAX_BIOME_DESCRIPTION_LENGTH)
        : undefined;
    const terrain = validateBiomeTerrainInfluence(biome.terrain, `${path}.terrain`);
    const materialValue = requireRecord(biome.material, `${path}.material`, ["surfaceId"]);
    const material = Object.freeze({
        surfaceId: requireIdentifier(materialValue.surfaceId, `${path}.material.surfaceId`, MAX_CATALOG_ID_LENGTH),
    });
    const scatterRuleIds = validateIdentifierList(biome.scatterRuleIds, `${path}.scatterRuleIds`, MAX_CATALOG_ID_LENGTH);
    const tags = validateIdentifierList(biome.tags, `${path}.tags`, MAX_CATALOG_ID_LENGTH);
    return description === undefined
        ? Object.freeze({
            id,
            version: WORLD_STUDIO_BIOME_VERSION,
            name,
            terrain,
            material,
            scatterRuleIds,
            tags,
        })
        : Object.freeze({
            id,
            version: WORLD_STUDIO_BIOME_VERSION,
            name,
            description,
            terrain,
            material,
            scatterRuleIds,
            tags,
        });
}
function validateWorldStudioBiomes(value) {
    if (!Array.isArray(value))
        invalid("$.biomes", "must be an array");
    if (value.length > MAX_BIOME_DEFINITIONS) {
        invalid("$.biomes", `must contain at most ${MAX_BIOME_DEFINITIONS} definitions`);
    }
    const ids = new Set();
    const biomes = value.map((entry, index) => {
        const biome = validateWorldStudioBiomeDefinition(entry, `$.biomes[${index}]`);
        if (ids.has(biome.id))
            invalid(`$.biomes[${index}].id`, "duplicate biome ID");
        ids.add(biome.id);
        return biome;
    });
    biomes.sort((a, b) => compareCanonicalStrings(a.id, b.id));
    return Object.freeze(biomes);
}
function validateBiomeTerrainInfluence(value, path) {
    if (value === null || typeof value !== "object" || Array.isArray(value))
        invalid(path, "must be an object");
    const mode = value.mode;
    if (mode === "inherit") {
        requireRecord(value, path, ["mode"]);
        return Object.freeze({ mode });
    }
    if (mode !== "modifier")
        invalid(`${path}.mode`, "unsupported terrain mode; expected inherit or modifier");
    const terrain = requireRecord(value, path, [
        "mode",
        "reliefMultiplier",
        "roughnessStrength",
        "localDetailStrength",
    ]);
    return Object.freeze({
        mode,
        reliefMultiplier: requireNumberRange(terrain.reliefMultiplier, `${path}.reliefMultiplier`, 0, 4),
        roughnessStrength: requireNumberRange(terrain.roughnessStrength, `${path}.roughnessStrength`, 0, 2),
        localDetailStrength: requireNumberRange(terrain.localDetailStrength, `${path}.localDetailStrength`, 0, 2),
    });
}
function validateIdentifierList(value, path, maximumLength) {
    if (!Array.isArray(value))
        invalid(path, "must be an array");
    if (value.length > MAX_BIOME_LIST_ENTRIES) {
        invalid(path, `must contain at most ${MAX_BIOME_LIST_ENTRIES} entries`);
    }
    const seen = new Set();
    const identifiers = value.map((entry, index) => {
        const identifier = requireIdentifier(entry, `${path}[${index}]`, maximumLength);
        if (seen.has(identifier))
            invalid(`${path}[${index}]`, "duplicate identifier");
        seen.add(identifier);
        return identifier;
    });
    identifiers.sort(compareCanonicalStrings);
    return Object.freeze(identifiers);
}
export function validateWorldStudioTerrainHeightDeltaLayer(value, grid, bounds, path = "$.layer") {
    const layer = requireRecord(value, path, ["id", "type", "version", "chunks"]);
    const id = requireIdentifier(layer.id, `${path}.id`, WORLD_STUDIO_PROJECT_ID_MAX_LENGTH);
    if (layer.type !== WORLD_STUDIO_TERRAIN_HEIGHT_DELTA_LAYER_TYPE)
        invalid(`${path}.type`, "unsupported layer type");
    if (layer.version !== WORLD_STUDIO_TERRAIN_HEIGHT_DELTA_LAYER_VERSION)
        invalid(`${path}.version`, "unsupported terrain height-delta version");
    if (!Array.isArray(layer.chunks))
        invalid(`${path}.chunks`, "must be an array");
    const samplesPerChunk = samplesPerChunkAxis(grid, path);
    const seenChunks = new Set();
    const chunks = [];
    for (let chunkIndex = 0; chunkIndex < layer.chunks.length; chunkIndex += 1) {
        const chunkPath = `${path}.chunks[${chunkIndex}]`;
        const chunk = requireRecord(layer.chunks[chunkIndex], chunkPath, ["x", "z", "samples"]);
        const x = requireSafeInteger(chunk.x, `${chunkPath}.x`);
        const z = requireSafeInteger(chunk.z, `${chunkPath}.z`);
        const chunkKey = `${x}:${z}`;
        if (seenChunks.has(chunkKey))
            invalid(chunkPath, "duplicate chunk");
        seenChunks.add(chunkKey);
        if (!Array.isArray(chunk.samples))
            invalid(`${chunkPath}.samples`, "must be an array");
        const seenSamples = new Set();
        const samples = [];
        for (let sampleIndex = 0; sampleIndex < chunk.samples.length; sampleIndex += 1) {
            const samplePath = `${chunkPath}.samples[${sampleIndex}]`;
            const sample = chunk.samples[sampleIndex];
            if (!Array.isArray(sample) || sample.length !== 3)
                invalid(samplePath, "must be [localX, localZ, deltaMeters]");
            const localX = requireSafeInteger(sample[0], `${samplePath}[0]`);
            const localZ = requireSafeInteger(sample[1], `${samplePath}[1]`);
            if (localX < 0 || localX >= samplesPerChunk)
                invalid(`${samplePath}[0]`, `must be between 0 and ${samplesPerChunk - 1}`);
            if (localZ < 0 || localZ >= samplesPerChunk)
                invalid(`${samplePath}[1]`, `must be between 0 and ${samplesPerChunk - 1}`);
            const sampleKey = `${localX}:${localZ}`;
            if (seenSamples.has(sampleKey))
                invalid(samplePath, "duplicate local sample");
            seenSamples.add(sampleKey);
            const deltaMeters = requireNumberRange(sample[2], `${samplePath}[2]`, -MAX_TERRAIN_DELTA_METERS, MAX_TERRAIN_DELTA_METERS);
            if (deltaMeters === 0)
                continue;
            const sampleX = x * samplesPerChunk + localX;
            const sampleZ = z * samplesPerChunk + localZ;
            if (!Number.isSafeInteger(sampleX) || !Number.isSafeInteger(sampleZ))
                invalid(samplePath, "global sample coordinate must be a safe integer");
            if (!isWithinWorldStudioBounds(bounds, sampleX * grid.sampleSpacingMeters, sampleZ * grid.sampleSpacingMeters)) {
                invalid(samplePath, "sample is outside min-inclusive, max-exclusive project bounds");
            }
            samples.push(Object.freeze([localX, localZ, deltaMeters]));
        }
        samples.sort((a, b) => a[1] - b[1] || a[0] - b[0]);
        if (samples.length > 0)
            chunks.push(Object.freeze({ x, z, samples: Object.freeze(samples) }));
    }
    chunks.sort((a, b) => a.z - b.z || a.x - b.x);
    return Object.freeze({
        id,
        type: WORLD_STUDIO_TERRAIN_HEIGHT_DELTA_LAYER_TYPE,
        version: WORLD_STUDIO_TERRAIN_HEIGHT_DELTA_LAYER_VERSION,
        chunks: Object.freeze(chunks),
    });
}
/** Persisted masks are already sparse and ordered; malformed input is never repaired on load. */
export function validateWorldStudioBiomeMaskLayer(value, grid, bounds, biomes, path = "$.layer") {
    return validateWorldStudioPaintMaskLayer(value, grid, bounds, new Set(biomes.map(({ id }) => id)), WORLD_STUDIO_BIOME_MASK_LAYER_TYPE, path);
}
export function validateWorldStudioGroundMaterialLayer(value, grid, bounds, path = "$.layer") {
    return validateWorldStudioPaintMaskLayer(value, grid, bounds, new Set(WORLD_STUDIO_GROUND_MATERIAL_IDS), WORLD_STUDIO_GROUND_MATERIAL_LAYER_TYPE, path);
}
/** Identical sparse lattice invariants, with independent layer type and identity registry. */
function validateWorldStudioPaintMaskLayer(value, grid, bounds, validIds, type, path) {
    const layer = requireRecord(value, path, ["id", "type", "version", "chunks"]);
    const id = requireIdentifier(layer.id, `${path}.id`, WORLD_STUDIO_PROJECT_ID_MAX_LENGTH);
    if (layer.type !== type)
        invalid(`${path}.type`, "unsupported layer type");
    if (layer.version !== WORLD_STUDIO_BIOME_MASK_LAYER_VERSION)
        invalid(`${path}.version`, "unsupported paint mask version");
    if (!Array.isArray(layer.chunks))
        invalid(`${path}.chunks`, "must be an array");
    const size = samplesPerChunkAxis(grid, path);
    const chunks = [];
    for (const [chunkIndex, chunkValue] of layer.chunks.entries()) {
        const chunkPath = `${path}.chunks[${chunkIndex}]`;
        const chunk = requireRecord(chunkValue, chunkPath, ["x", "z", "samples"]);
        const x = requireSafeInteger(chunk.x, `${chunkPath}.x`);
        const z = requireSafeInteger(chunk.z, `${chunkPath}.z`);
        if (!Number.isSafeInteger(x * size) || !Number.isSafeInteger(z * size))
            invalid(chunkPath, "global sample coordinate must be a safe integer");
        const previousChunk = chunks.at(-1);
        if (previousChunk && (z < previousChunk.z || (z === previousChunk.z && x <= previousChunk.x))) {
            invalid(chunkPath, "duplicate chunk or invalid canonical z/x ordering");
        }
        if (!Array.isArray(chunk.samples) || chunk.samples.length === 0)
            invalid(`${chunkPath}.samples`, "must be a nonempty array");
        const samples = [];
        for (const [sampleIndex, sample] of chunk.samples.entries()) {
            const samplePath = `${chunkPath}.samples[${sampleIndex}]`;
            if (!Array.isArray(sample) || sample.length !== 3)
                invalid(samplePath, "must be [localX, localZ, weights]");
            const localX = requireSafeInteger(sample[0], `${samplePath}[0]`);
            const localZ = requireSafeInteger(sample[1], `${samplePath}[1]`);
            if (localX < 0 || localX >= size || localZ < 0 || localZ >= size)
                invalid(samplePath, `local coordinates must be between 0 and ${size - 1}`);
            const previousSample = samples.at(-1);
            if (previousSample && (localZ < previousSample[1] || (localZ === previousSample[1] && localX <= previousSample[0]))) {
                invalid(samplePath, "duplicate sample or invalid canonical z/x ordering");
            }
            const sampleX = x * size + localX;
            const sampleZ = z * size + localZ;
            if (!Number.isSafeInteger(sampleX) || !Number.isSafeInteger(sampleZ))
                invalid(samplePath, "global sample coordinate must be a safe integer");
            if (!isWithinWorldStudioBounds(bounds, sampleX * grid.sampleSpacingMeters, sampleZ * grid.sampleSpacingMeters))
                invalid(samplePath, "sample is outside project authoring bounds");
            if (!Array.isArray(sample[2]) || sample[2].length === 0)
                invalid(`${samplePath}[2]`, "must be nonempty biome weights");
            const weights = [];
            let sum = 0;
            for (const [weightIndex, entry] of sample[2].entries()) {
                const weightPath = `${samplePath}[2][${weightIndex}]`;
                if (!Array.isArray(entry) || entry.length !== 2)
                    invalid(weightPath, "must be [biomeId, weight]");
                const biomeId = validateWorldStudioBiomeId(entry[0], `${weightPath}[0]`);
                if (!validIds.has(biomeId))
                    invalid(`${weightPath}[0]`, type === WORLD_STUDIO_BIOME_MASK_LAYER_TYPE ? "references nonexistent project biome" : "unsupported ground material ID");
                const weight = requireNumberRange(entry[1], `${weightPath}[1]`, 0, 1);
                if (weight <= WORLD_STUDIO_MASK_WEIGHT_EPSILON)
                    invalid(`${weightPath}[1]`, "weight must exceed sparse epsilon");
                const previous = weights.at(-1);
                if (previous && compareCanonicalStrings(previous[0], biomeId) >= 0)
                    invalid(weightPath, "duplicate biome ID or invalid canonical ordering");
                sum += weight;
                weights.push(Object.freeze([biomeId, weight]));
            }
            if (sum > 1)
                invalid(`${samplePath}[2]`, "authored weights must sum to at most 1");
            samples.push(Object.freeze([localX, localZ, Object.freeze(weights)]));
        }
        chunks.push(Object.freeze({ x, z, samples: Object.freeze(samples) }));
    }
    return Object.freeze({ id, type, version: WORLD_STUDIO_BIOME_MASK_LAYER_VERSION, chunks: Object.freeze(chunks) });
}
export function validateWorldStudioFoundationGradeLayer(value, bounds, path = "$.foundation") {
    const entry = requireRecord(value, path, ["id", "type", "version", "instanceId", "assetId", "center", "yawDeg", "footprint", "targetDatumMeters", "falloffMeters"]);
    const id = requireIdentifier(entry.id, `${path}.id`, WORLD_STUDIO_PROJECT_ID_MAX_LENGTH);
    if (entry.type !== WORLD_STUDIO_FOUNDATION_GRADE_LAYER_TYPE)
        invalid(`${path}.type`, "unsupported foundation layer type");
    if (entry.version !== WORLD_STUDIO_FOUNDATION_GRADE_LAYER_VERSION)
        invalid(`${path}.version`, "unsupported foundation grade version");
    // Reuse exactly the authored identity/transform contract without inventing load-time targets.
    const identity = validateWorldStudioAuthoredInstance({ id: entry.instanceId, assetId: entry.assetId, position: entry.center, yawDeg: entry.yawDeg, heightOffsetMeters: 0 }, bounds, path);
    const source = entry.footprint;
    if (source === null || typeof source !== "object" || Array.isArray(source))
        invalid(`${path}.footprint`, "must be an object");
    const kind = source.kind;
    let footprint;
    if (kind === "rectangle") {
        const shape = requireRecord(source, `${path}.footprint`, ["kind", "widthMeters", "depthMeters"]);
        const widthMeters = requireFiniteNumber(shape.widthMeters, `${path}.footprint.widthMeters`);
        const depthMeters = requireFiniteNumber(shape.depthMeters, `${path}.footprint.depthMeters`);
        if (widthMeters <= 0 || depthMeters <= 0)
            invalid(`${path}.footprint`, "dimensions must be positive");
        footprint = Object.freeze({ kind, widthMeters, depthMeters });
    }
    else if (kind === "circle") {
        const shape = requireRecord(source, `${path}.footprint`, ["kind", "radiusMeters"]);
        const radiusMeters = requireFiniteNumber(shape.radiusMeters, `${path}.footprint.radiusMeters`);
        if (radiusMeters <= 0)
            invalid(`${path}.footprint.radiusMeters`, "must be positive");
        footprint = Object.freeze({ kind, radiusMeters });
    }
    else
        invalid(`${path}.footprint.kind`, "unsupported foundation footprint");
    const targetDatumMeters = requireFiniteNumber(entry.targetDatumMeters, `${path}.targetDatumMeters`);
    const falloffMeters = requireFiniteNumber(entry.falloffMeters, `${path}.falloffMeters`);
    if (falloffMeters <= 0)
        invalid(`${path}.falloffMeters`, "must be positive");
    const envelope = footprintWorldBounds({ footprint, position: identity.position, yawDeg: identity.yawDeg }, falloffMeters);
    if (![envelope.minX, envelope.minZ, envelope.maxX, envelope.maxZ].every(Number.isFinite)
        || envelope.minX < bounds.minX || envelope.minZ < bounds.minZ || envelope.maxX >= bounds.maxX || envelope.maxZ >= bounds.maxZ) {
        invalid(path, "foundation footprint and falloff must lie entirely within project bounds");
    }
    return Object.freeze({ id, type: WORLD_STUDIO_FOUNDATION_GRADE_LAYER_TYPE, version: WORLD_STUDIO_FOUNDATION_GRADE_LAYER_VERSION,
        instanceId: identity.id, assetId: identity.assetId, center: identity.position, yawDeg: identity.yawDeg, footprint, targetDatumMeters, falloffMeters });
}
/** Legacy instances without an operator remain untouched; every operator must have one exact pair. */
function validateFoundationPairing(layers, instances, chunkSize) {
    const byId = new Map(instances.map((instance) => [instance.id, instance]));
    const paired = new Set();
    const buckets = new Map();
    let indexedEntries = 0;
    for (const layer of layers) {
        if (layer.type !== WORLD_STUDIO_FOUNDATION_GRADE_LAYER_TYPE)
            continue;
        const path = `$.layers.${layer.id}`;
        const instance = byId.get(layer.instanceId);
        if (!instance)
            invalid(path, "orphaned foundation operator has no authored instance");
        if ((instance.scale ?? 1) !== 1)
            invalid(path, "foundation paired instances require scale 1");
        if (paired.has(layer.instanceId))
            invalid(path, "duplicate foundation operator for authored instance");
        if (instance.assetId !== layer.assetId || instance.position.x !== layer.center.x || instance.position.z !== layer.center.z || instance.yawDeg !== layer.yawDeg) {
            invalid(path, "foundation asset, center and yaw must match its authored instance");
        }
        paired.add(layer.instanceId);
        const shape = { footprint: layer.footprint, position: layer.center, yawDeg: layer.yawDeg };
        const bounds = footprintWorldBounds(shape, layer.falloffMeters + FOOTPRINT_EDGE_EPSILON_METERS);
        const minX = Math.floor(bounds.minX / chunkSize), maxX = Math.floor(bounds.maxX / chunkSize);
        const minZ = Math.floor(bounds.minZ / chunkSize), maxZ = Math.floor(bounds.maxZ / chunkSize);
        const count = (maxX - minX + 1) * (maxZ - minZ + 1);
        if (![minX, minZ, maxX, maxZ].every(Number.isSafeInteger) || !Number.isSafeInteger(count) || count > WORLD_STUDIO_MAX_FOUNDATION_CHUNKS)
            invalid(path, "foundation spatial index budget exceeded");
        indexedEntries += count;
        if (indexedEntries > 1_000_000)
            invalid(path, "project foundation spatial index budget exceeded");
        const tested = new Set();
        for (let z = minZ; z <= maxZ; z += 1)
            for (let x = minX; x <= maxX; x += 1) {
                const key = `${x},${z}`;
                const bucket = buckets.get(key) ?? [];
                for (const other of bucket) {
                    if (tested.has(other.id))
                        continue;
                    tested.add(other.id);
                    if (footprintsOverlap(shape, { footprint: other.footprint, position: other.center, yawDeg: other.yawDeg }, layer.falloffMeters, other.falloffMeters)) {
                        invalid(path, `unsupported foundation grading envelope overlap with ${other.id}`);
                    }
                }
                bucket.push(layer);
                buckets.set(key, bucket);
            }
    }
}
function validateWorldStudioLayers(value, grid, bounds, biomes) {
    if (!Array.isArray(value))
        invalid("$.layers", "must be an array");
    const ids = new Set();
    const layers = value.map((entry, index) => {
        if (entry === null || typeof entry !== "object" || Array.isArray(entry))
            invalid(`$.layers[${index}]`, "must be an object");
        const record = entry;
        const layer = record.type === WORLD_STUDIO_TERRAIN_HEIGHT_DELTA_LAYER_TYPE
            ? validateWorldStudioTerrainHeightDeltaLayer(entry, grid, bounds, `$.layers[${index}]`)
            : record.type === WORLD_STUDIO_BIOME_MASK_LAYER_TYPE
                ? validateWorldStudioBiomeMaskLayer(entry, grid, bounds, biomes, `$.layers[${index}]`)
                : record.type === WORLD_STUDIO_GROUND_MATERIAL_LAYER_TYPE
                    ? validateWorldStudioGroundMaterialLayer(entry, grid, bounds, `$.layers[${index}]`)
                    : record.type === WORLD_STUDIO_FOUNDATION_GRADE_LAYER_TYPE
                        ? validateWorldStudioFoundationGradeLayer(entry, bounds, `$.layers[${index}]`)
                        : record.type === WORLD_STUDIO_SCATTER_EXCLUSION_LAYER_TYPE
                            ? validateWorldStudioScatterExclusionLayer(entry, grid, bounds, `$.layers[${index}]`)
                            : invalid(`$.layers[${index}].type`, "unsupported layer type");
        if (ids.has(layer.id))
            invalid(`$.layers[${index}].id`, "duplicate layer ID");
        ids.add(layer.id);
        return layer;
    });
    if (layers.filter((layer) => layer.type === WORLD_STUDIO_SCATTER_EXCLUSION_LAYER_TYPE).length > 1) {
        invalid("$.layers", "only one manual scatter exclusion layer is supported");
    }
    // Foundation composition is a separate non-overlapping stage; its serialization order is ID-based.
    // Preserve the established relative order of every legacy manual/paint layer.
    return Object.freeze([...layers.filter((layer) => layer.type !== WORLD_STUDIO_FOUNDATION_GRADE_LAYER_TYPE),
        ...layers.filter((layer) => layer.type === WORLD_STUDIO_FOUNDATION_GRADE_LAYER_TYPE).sort((a, b) => compareCanonicalStrings(a.id, b.id))]);
}
function samplesPerChunkAxis(grid, path) {
    const exact = grid.chunkSizeMeters / grid.sampleSpacingMeters;
    if (!Number.isSafeInteger(exact) || exact < 1)
        invalid(`${path}.chunks`, "grid requires an integer samples-per-chunk ratio");
    return exact;
}
/** Replaces only the terrain base and increments revision; the result is canonical and frozen. */
export function createWorldStudioProjectForPreset(source, preset) {
    const project = validateWorldStudioProject(source);
    const base = preset === "flat"
        ? { mode: WORLD_STUDIO_BASE_MODE, heightMeters: 0 }
        : {
            mode: WORLD_STUDIO_GENERATED_BASE_MODE,
            datumMeters: 0,
            recipe: preset === "rolling-hills"
                ? DEFAULT_ROLLING_HILLS_RECIPE
                : preset === "mountain-ridge"
                    ? DEFAULT_MOUNTAIN_RIDGE_RECIPE
                    : DEFAULT_CRATER_BASIN_RECIPE,
        };
    return validateWorldStudioProject({ ...project, revision: project.revision + 1, base });
}
/** Applies one accepted inspector regenerate as exactly one canonical project revision. */
export function createWorldStudioProjectWithTerrainConfiguration(source, update) {
    const project = validateWorldStudioProject(source);
    return validateWorldStudioProject({
        ...project,
        revision: project.revision + 1,
        generator: { ...project.generator, seed: update.seed },
        base: update.base,
    });
}
function validateBase(value) {
    if (value === null || typeof value !== "object" || Array.isArray(value))
        invalid("$.base", "must be an object");
    const mode = value.mode;
    if (mode === WORLD_STUDIO_BASE_MODE) {
        const base = requireRecord(value, "$.base", ["mode", "heightMeters"]);
        return Object.freeze({ mode: WORLD_STUDIO_BASE_MODE, heightMeters: requireFiniteNumber(base.heightMeters, "$.base.heightMeters") });
    }
    if (mode !== WORLD_STUDIO_GENERATED_BASE_MODE)
        invalid("$.base.mode", "unsupported base mode; expected flat or generated");
    const base = requireRecord(value, "$.base", ["mode", "datumMeters", "recipe"]);
    return Object.freeze({
        mode: WORLD_STUDIO_GENERATED_BASE_MODE,
        datumMeters: requireFiniteNumber(base.datumMeters, "$.base.datumMeters"),
        recipe: validateRecipe(base.recipe),
    });
}
function validateRecipe(value) {
    if (value === null || typeof value !== "object" || Array.isArray(value))
        invalid("$.base.recipe", "must be an object");
    const id = value.id;
    if (id === "rolling-hills") {
        const recipe = requireRecord(value, "$.base.recipe", ["id", "reliefMeters", "macroWavelengthMeters", "regionalWavelengthMeters", "localWavelengthMeters", "regionalStrength", "localStrength", "warpMeters"]);
        return Object.freeze({
            id,
            reliefMeters: requireNumberRange(recipe.reliefMeters, "$.base.recipe.reliefMeters", 1, 2_000),
            macroWavelengthMeters: requireNumberRange(recipe.macroWavelengthMeters, "$.base.recipe.macroWavelengthMeters", 16, 100_000),
            regionalWavelengthMeters: requireNumberRange(recipe.regionalWavelengthMeters, "$.base.recipe.regionalWavelengthMeters", 4, 25_000),
            localWavelengthMeters: requireNumberRange(recipe.localWavelengthMeters, "$.base.recipe.localWavelengthMeters", 1, 5_000),
            regionalStrength: requireNumberRange(recipe.regionalStrength, "$.base.recipe.regionalStrength", 0, 2),
            localStrength: requireNumberRange(recipe.localStrength, "$.base.recipe.localStrength", 0, 1),
            warpMeters: requireNumberRange(recipe.warpMeters, "$.base.recipe.warpMeters", 0, 10_000),
        });
    }
    if (id === "mountain-ridge") {
        const recipe = requireRecord(value, "$.base.recipe", ["id", "reliefMeters", "macroWavelengthMeters", "ridgeWavelengthMeters", "regionalWavelengthMeters", "ridgeSharpness", "valleyStrength", "localStrength", "warpMeters"]);
        return Object.freeze({
            id,
            reliefMeters: requireNumberRange(recipe.reliefMeters, "$.base.recipe.reliefMeters", 10, 4_000),
            macroWavelengthMeters: requireNumberRange(recipe.macroWavelengthMeters, "$.base.recipe.macroWavelengthMeters", 32, 100_000),
            ridgeWavelengthMeters: requireNumberRange(recipe.ridgeWavelengthMeters, "$.base.recipe.ridgeWavelengthMeters", 8, 25_000),
            regionalWavelengthMeters: requireNumberRange(recipe.regionalWavelengthMeters, "$.base.recipe.regionalWavelengthMeters", 2, 10_000),
            ridgeSharpness: requireNumberRange(recipe.ridgeSharpness, "$.base.recipe.ridgeSharpness", 0.5, 8),
            valleyStrength: requireNumberRange(recipe.valleyStrength, "$.base.recipe.valleyStrength", 0, 2),
            localStrength: requireNumberRange(recipe.localStrength, "$.base.recipe.localStrength", 0, 1),
            warpMeters: requireNumberRange(recipe.warpMeters, "$.base.recipe.warpMeters", 0, 10_000),
        });
    }
    if (id === "crater-basin") {
        const recipe = requireRecord(value, "$.base.recipe", ["id", "centerX", "centerZ", "radiusMeters", "depthMeters", "rimHeightMeters", "rimWidthMeters", "floorRadiusMeters"]);
        const radiusMeters = requireNumberRange(recipe.radiusMeters, "$.base.recipe.radiusMeters", 1, 100_000);
        const floorRadiusMeters = requireNumberRange(recipe.floorRadiusMeters, "$.base.recipe.floorRadiusMeters", 0, radiusMeters * 0.9);
        return Object.freeze({
            id,
            centerX: requireFiniteNumber(recipe.centerX, "$.base.recipe.centerX"),
            centerZ: requireFiniteNumber(recipe.centerZ, "$.base.recipe.centerZ"),
            radiusMeters,
            depthMeters: requireNumberRange(recipe.depthMeters, "$.base.recipe.depthMeters", Number.MIN_VALUE, 10_000),
            rimHeightMeters: requireNumberRange(recipe.rimHeightMeters, "$.base.recipe.rimHeightMeters", 0, 10_000),
            rimWidthMeters: requireNumberRange(recipe.rimWidthMeters, "$.base.recipe.rimWidthMeters", 0.1, radiusMeters),
            floorRadiusMeters,
        });
    }
    invalid("$.base.recipe.id", "unsupported recipe");
}
function requireNumberRange(value, path, minimum, maximum) {
    const number = requireFiniteNumber(value, path);
    if (number < minimum || number > maximum)
        invalid(path, `must be between ${minimum} and ${maximum}`);
    return number;
}
export function parseWorldStudioProject(serialized) {
    if (typeof serialized !== "string")
        invalid("$", "serialized project must be a string");
    let value;
    try {
        value = JSON.parse(serialized);
    }
    catch {
        invalid("$", "invalid JSON");
    }
    return validateWorldStudioProject(value);
}
export function serializeWorldStudioProject(value) {
    const project = validateWorldStudioProject(value);
    return `${JSON.stringify(project, null, 2)}\n`;
}
/** Bounds are min-inclusive and max-exclusive; sampling itself is not clamped. */
export function isWithinWorldStudioBounds(bounds, x, z) {
    return Number.isFinite(x)
        && Number.isFinite(z)
        && x >= bounds.minX
        && x < bounds.maxX
        && z >= bounds.minZ
        && z < bounds.maxZ;
}
function requireRecord(value, path, expectedKeys, optionalKeys = []) {
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
        invalid(path, "must be an object");
    }
    const prototype = Object.getPrototypeOf(value);
    if (prototype !== Object.prototype && prototype !== null)
        invalid(path, "must be a plain object");
    const record = value;
    for (const key of expectedKeys) {
        if (!Object.hasOwn(record, key))
            invalid(`${path}.${key}`, "is required");
    }
    const expected = new Set([...expectedKeys, ...optionalKeys]);
    for (const key of Object.keys(record)) {
        if (!expected.has(key))
            invalid(`${path}.${key}`, "unknown field");
    }
    return record;
}
function requireDisplayText(value, path, maximumLength) {
    if (typeof value !== "string"
        || value.length < 1
        || value.length > maximumLength
        || value.trim() !== value
        || CONTROL_CHARACTER_PATTERN.test(value)) {
        invalid(path, `must be non-blank trimmed text without control characters, at most ${maximumLength} characters`);
    }
    return value;
}
function compareCanonicalStrings(a, b) {
    return a < b ? -1 : a > b ? 1 : 0;
}
function requireIdentifier(value, path, maximumLength) {
    if (typeof value !== "string"
        || value.length < 1
        || value.length > maximumLength
        || !PROJECT_ID_PATTERN.test(value)) {
        invalid(path, `must be a lowercase slug of at most ${maximumLength} characters`);
    }
    return value;
}
function requireFiniteNumber(value, path) {
    if (typeof value !== "number" || !Number.isFinite(value))
        invalid(path, "must be a finite number");
    return value === 0 ? 0 : value;
}
function requireSafeInteger(value, path) {
    if (typeof value !== "number" || !Number.isSafeInteger(value))
        invalid(path, "must be a safe integer");
    return value === 0 ? 0 : value;
}
function requireNonNegativeSafeInteger(value, path) {
    const integer = requireSafeInteger(value, path);
    if (integer < 0)
        invalid(path, "must be non-negative");
    return integer;
}
function requirePositiveSafeInteger(value, path) {
    const integer = requireSafeInteger(value, path);
    if (integer < 1)
        invalid(path, "must be positive");
    return integer;
}
function requireEmptyArray(value, path) {
    if (!Array.isArray(value))
        invalid(path, "must be an array");
    if (value.length !== 0)
        invalid(path, "entries are not supported yet");
}
function invalid(path, reason) {
    throw new WorldStudioProjectValidationError(path, reason);
}
